import { API_BASE_URL } from './client';

export type RevealItem = {
  submissionId: string;
  label: string;
  audioUrl: string;
  durationSec: number | null;
  // True when the API recognises this submission as the caller's own.
  // The vote screen disables scoring and labels it; the API also drops
  // self-votes silently as a server-side defense.
  isOwn: boolean;
};

export type VotableMatch = {
  roomCode: string;
  mode: 'quickplay' | 'ranked' | 'private' | 'flip' | 'daily' | 'tournament';
  phase: string;
  transitionsAt: number | null;
  genre: { slug: string; name: string };
  submissions: RevealItem[];
};

export async function fetchVotableMatches(): Promise<VotableMatch[]> {
  // credentials: 'include' carries the session cookie so the server can flag
  // each submission's `isOwn` and the UI can disable self-voting.
  const res = await fetch(new URL('/audience/matches', API_BASE_URL), {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`audience matches ${res.status}`);
  const json = (await res.json()) as { items: VotableMatch[] };
  return json.items;
}

export async function fetchSamplePackZipUrl(code: string): Promise<string | null> {
  const res = await fetch(new URL(`/matches/${code}/sample-pack/zip`, API_BASE_URL));
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`sample-pack zip ${res.status}`);
  const json = (await res.json()) as { url: string };
  return json.url;
}

export type ResultItem = {
  rank: number;
  submissionId: string;
  handle: string;
  title: string | null;
  audioUrl: string;
  score: number;
};

export async function fetchReveal(code: string, user?: string | null): Promise<RevealItem[]> {
  // ?user=<handle> lets the API compute isOwn for guest callers (no
  // session cookie). Authenticated requests can omit it; the session wins.
  const url = new URL(`/matches/${code}/reveal`, API_BASE_URL);
  if (user) url.searchParams.set('user', user);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`reveal ${res.status}`);
  const json = (await res.json()) as { items: RevealItem[] };
  return json.items;
}

export async function fetchResults(code: string): Promise<ResultItem[]> {
  const res = await fetch(new URL(`/matches/${code}/results`, API_BASE_URL));
  if (!res.ok) throw new Error(`results ${res.status}`);
  const json = (await res.json()) as { items: ResultItem[] };
  return json.items;
}

export async function castVotes(params: {
  code: string;
  user: string;
  votes: Array<{ submissionId: string; score: number }>;
}): Promise<{ accepted: number }> {
  const res = await fetch(new URL(`/rooms/${params.code}/vote`, API_BASE_URL), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ user: params.user, votes: params.votes }),
  });
  if (!res.ok) throw new Error(`vote ${res.status}: ${await res.text()}`);
  return res.json() as Promise<{ accepted: number }>;
}
